/**
 * Tracks chat turn timing milestones and reports them to Datadog RUM.
 *
 * Watches the `useChat` status lifecycle (`submitted` → `streaming` → `ready`)
 * and the assistant message content to capture four key timestamps per turn:
 *
 * | Milestone        | When it fires                                         |
 * |------------------|-------------------------------------------------------|
 * | `t_submit`       | Status transitions to `"submitted"`                   |
 * | `t_streaming`    | Status transitions to `"streaming"` (first SSE byte)  |
 * | `t_first_content`| First visible text, reasoning, or spec in assistant parts |
 * | `t_complete`     | Status transitions to `"ready"` (stream finished)     |
 *
 * Derived metrics reported via `datadogRum.addAction("herald_chat_turn")`:
 * - **network_ms** — `t_streaming - t_submit` (POST roundtrip + server setup)
 * - **ttft_ui_ms** — `t_first_content - t_submit` (user-perceived wait for content)
 * - **stream_ms** — `t_complete - t_streaming` (LLM generation + tool calls)
 * - **total_ms** — `t_complete - t_submit` (end-to-end turn duration)
 *
 * All timing uses `performance.now()` for sub-millisecond precision and
 * monotonic guarantees (immune to system clock adjustments).
 *
 * The hook is intentionally fire-and-forget — it never causes re-renders
 * (all state lives in refs) and silently no-ops if Datadog is unavailable.
 */
import type { UIMessage } from "@ai-sdk/react";
import { useEffect, useRef } from "react";
import { loadDatadog } from "@/lib/analytics/datadog";

interface TurnState {
  /** Monotonic timestamp when the user pressed send. */
  tSubmit: number;
  /** Monotonic timestamp when first SSE byte arrived (status → streaming). */
  tStreaming: number | null;
  /** Monotonic timestamp when first visible content appeared in the assistant message. */
  tFirstContent: number | null;
  /** Whether this turn has already been reported (prevents double-fire). */
  reported: boolean;
}

const createFreshTurn = (tSubmit: number): TurnState => ({
  tSubmit,
  tStreaming: null,
  tFirstContent: null,
  reported: false,
});

const getLastUserMessageIndex = (messages: UIMessage[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === "user") {
      return i;
    }
  }

  return -1;
};

/**
 * Returns true when the latest assistant message has visible content —
 * at least one text part with non-empty text, a `reasoning` part
 * (extended thinking streamed before tool calls), or a `data-spec` part
 * (rich UI component being assembled by json-render).
 *
 * Why `reasoning` counts: reasoning tokens arrive seconds before the first
 * `text` delta (the agent thinks, then calls tools, then writes). Counting
 * them as visible drops the measured TTFT from ~12 s to ~4.5 s, which
 * matches the moment the user sees the "Thinking…" shimmer replaced by
 * streaming activity in the UI.
 *
 * Generic `"data"` parts are excluded because they can represent non-visible
 * metadata (tool call inputs/outputs, internal chunks) that appear before
 * any user-facing content is rendered by {@link AssistantMessageContent}.
 */
const hasVisibleAssistantContent = (messages: UIMessage[]): boolean => {
  // Only check assistant messages AFTER the last user message — that's the
  // current turn's response.  Without this bound the function finds stale
  // content from a previous turn and immediately returns true, producing
  // ttft_ui_ms: 0 on every multi-turn conversation.
  const lastUserIdx = getLastUserMessageIndex(messages);

  for (let i = messages.length - 1; i > lastUserIdx; i--) {
    const msg = messages[i];
    if (msg == null || msg.role !== "assistant") continue;

    const parts =
      "parts" in msg
        ? (msg.parts as Array<Record<string, unknown> & { type: string }>)
        : [];
    for (const part of parts) {
      if (
        part.type === "text" &&
        typeof part.text === "string" &&
        part.text.length > 0
      ) {
        return true;
      }
      if (part.type === "reasoning") {
        return true;
      }
      if (part.type === "data-spec") {
        return true;
      }
    }
    // Only check the latest assistant message per turn.
    break;
  }
  return false;
};

/** Round to 1 decimal place — matches the backend's `elapsed_ms` precision. */
const round1 = (ms: number): number => Math.round(ms * 10) / 10;

interface UseChatTimingParams {
  /** Current status from `useChat`. */
  status: string;
  /** Current messages array from `useChat`. */
  messages: UIMessage[];
  /** Conversation id (attached to the RUM action for cross-referencing with backend logs). */
  conversationId?: string | null;
}

/**
 * Tracks per-turn chat timing and reports to Datadog RUM on completion.
 *
 * Does not return anything — this hook is purely observational.
 * All state lives in refs so it never triggers re-renders.
 */
const useChatTiming = ({
  status,
  messages,
  conversationId,
}: UseChatTimingParams): void => {
  const turnRef = useRef<TurnState | null>(null);
  const prevStatusRef = useRef<string>(status);
  // Ref-backed snapshot so the status effect can read messages.length
  // at report time without including it in the dependency array.
  // During streaming every new chunk changes messages.length, which would
  // re-fire the status effect on every append (harmless due to the
  // same-status early-return, but wasteful).
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Track status transitions.
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;

    // Same status — nothing to do.
    if (status === prevStatus) return;

    const now = performance.now();

    if (status === "submitted") {
      // New turn started — reset tracking.
      turnRef.current = createFreshTurn(now);
      return;
    }

    const turn = turnRef.current;
    if (turn == null || turn.reported) return;

    if (status === "streaming" && turn.tStreaming == null) {
      turn.tStreaming = now;
    }

    if (status === "ready" || status === "error") {
      // Capture first content if it arrived in this same render cycle.
      // React runs effects in declaration order, so the messages effect
      // below has not fired yet. Without this inline check, tFirstContent
      // would be null for fast turns where the first content and the
      // terminal status land in the same commit.
      if (
        turn.tFirstContent == null &&
        hasVisibleAssistantContent(messagesRef.current)
      ) {
        turn.tFirstContent = now;
      }

      // Turn complete — report metrics.
      turn.reported = true;
      const tComplete = now;
      const isError = status === "error";

      const totalMs = round1(tComplete - turn.tSubmit);
      const networkMs =
        turn.tStreaming != null ? round1(turn.tStreaming - turn.tSubmit) : null;
      const streamMs =
        turn.tStreaming != null ? round1(tComplete - turn.tStreaming) : null;
      const ttftUiMs =
        turn.tFirstContent != null
          ? round1(turn.tFirstContent - turn.tSubmit)
          : null;

      const payload = {
        total_ms: totalMs,
        network_ms: networkMs,
        stream_ms: streamMs,
        ttft_ui_ms: ttftUiMs,
        is_error: isError,
        conversation_id: conversationId ?? undefined,
        message_count: messagesRef.current.length,
      };

      // Fire-and-forget — never block the UI.
      loadDatadog().then((datadogRum) => {
        datadogRum.addAction("herald_chat_turn", payload);
      });

      if (import.meta.env.DEV) {
        console.debug("[useChatTiming] herald_chat_turn", payload);
      }
    }
  }, [status, conversationId]);

  // Track first visible content independently — messages can update many
  // times while status stays "streaming", so this needs its own effect.
  useEffect(() => {
    const turn = turnRef.current;
    if (turn == null || turn.reported || turn.tFirstContent != null) return;

    if (hasVisibleAssistantContent(messages)) {
      turn.tFirstContent = performance.now();
    }
  }, [messages]);
};

export default useChatTiming;
